import React, { useState } from "react";
import { EFootprint } from "../../../../Model/PlateType";
import CheckBox from "../../../Buttons/CheckBox";
import {
  SlotAddress,
  SlotCorrectionResult,
} from "../../../../Model/SlotCorrection";
import { StorageConfig, HotelConfig } from "../../../../Model/StorageConfig";
import { EModuleType } from "../../../../Model/EModuleType";
import { getModulesByType } from "../../../../redux/reducers/ModuleReducer";
import { getStorageConfig } from "../../../../redux/reducers/StorageConfigReducer";
import { useTypedSelector } from "../../../../redux/reducers/RootReducer";
import "./SlotCorrection.css";
import { PlateAddress } from "../../../../Model/PlateAddress";
import { getPlateAddresses } from "../../../../redux/reducers/PlateAddressReducer";
import { ESlotCorrectionStatus } from "../../../../Model/ESlotCorrectionStatus";

const getHotels = (storageConfig: StorageConfig) => {
  let hotels = [];
  for (let i = 0; i < storageConfig.hotels.length; i++) {
    hotels.push({
      id: i,
      name: `hotel-${EFootprint[storageConfig.hotels[i].footprint]}-${
        storageConfig.hotels[i].number
      }`,
      display: `Hotel ${EFootprint[storageConfig.hotels[i].footprint]} ${
        storageConfig.hotels[i].number
      }`,
    });
  }
  return hotels;
};

const isSlotOccupied = (plateAddress: PlateAddress | undefined) => {
  if (undefined) {
    return false;
  }

  if (plateAddress?.usedByPlate) {
    return true;
  }

  return false;
};

const isSlotError = (
  plateAddress: PlateAddress | undefined,
  correctionResults: SlotCorrectionResult[]
) => {
  if (plateAddress === undefined) {
    return true;
  }

  const currentSlotCorrection = correctionResults.find(
    (sc) =>
      sc.moduleIdentifier == plateAddress.moduleIdentifier &&
      sc.hotelNumber == plateAddress.hotelNo &&
      sc.slotNumber == plateAddress.slotNo &&
      sc.status == ESlotCorrectionStatus.Error
  );

  if (currentSlotCorrection !== undefined) {
    return true;
  }

  return false;
};

const getSlots = (
  hotelConfig: HotelConfig,
  plateAddresses: PlateAddress[],
  slotCorrectionResults: SlotCorrectionResult[]
) => {
  let slots = [];
  let disabledSlotsList: number[] | null =
    hotelConfig.disabledSlots == null
      ? null
      : hotelConfig.disabledSlots?.split(",").map(Number);
  if (hotelConfig !== undefined) {
    for (let i: number = 0; i < hotelConfig.capacity; i++) {
      let slotIndex: number = i + 1;
      const plateAddress = plateAddresses.find(
        (pa) => pa.hotelNo === hotelConfig.number && pa.slotNo == slotIndex
      );
      slots.push({
        id: i,
        name: `${hotelConfig.footprint}-${hotelConfig.number}-slot-${i}`,
        display: `Slot ${slotIndex}`,
        disabled:
          disabledSlotsList?.includes(slotIndex) ||
          isSlotOccupied(plateAddress) || isSlotError(plateAddress, slotCorrectionResults),
      });
    }
  }
  return slots;
};

type Prop = {
  setSlotAddress: (value: SlotAddress[]) => void;
  slotAddress: SlotAddress[];
  moduleIdentifier: string;
  slotCorrectionResults: SlotCorrectionResult[]
};
export default function AllHotelContent({
  setSlotAddress,
  slotAddress,
  moduleIdentifier,
  slotCorrectionResults
}: Prop) {
  const storageModule = useTypedSelector(
    (state) => getModulesByType(state)[EModuleType.CHAMBER][0]
  );
  const storageConfig = useTypedSelector((state) =>
    getStorageConfig(state, storageModule.identifier)
  );

  const dataHotel = getHotels(storageConfig);

  const [selectedHotel, setSelectedHotel] = useState<string[]>([]);
  const plateAddresses = useTypedSelector((state) => getPlateAddresses(state));

  const handleSelectAllChange = () => {
    if (selectedHotel.length === dataHotel.length) {
      setSlotAddress([]);
      setSelectedHotel([]);
    } else {
      setSelectedHotel(
        dataHotel.map((_, index) => `hotel${index + 1}-slot-correction`)
      );
      setSlotAddress([...arrayHotelAddresses()]);
    }
  };

  const handleHotelChange = (hotel: number) => {
    const newSelectedHotel = `hotel${hotel + 1}-slot-correction`;
    const existingIndex = selectedHotel.findIndex(
      (item) => item === newSelectedHotel
    );
    if (existingIndex !== -1) {
      const updatedCheckboxes = [...selectedHotel];
      setSelectedHotel(
        updatedCheckboxes.filter((_, index) => index !== existingIndex)
      );
      const updatedSlotAddress = [...slotAddress];
      setSlotAddress(
        updatedSlotAddress.filter((item) => item.hotelNumber !== hotel + 1)
      );
    } else {
      const updatedCheckboxes = [...selectedHotel, newSelectedHotel];
      setSelectedHotel(updatedCheckboxes);
      setSlotAddress([
        ...slotAddress.filter((item) => item.hotelNumber !== hotel + 1),
        ...arraySlotAddresses(hotel),
      ]);
    }
  };

  const arraySlotAddresses = (hotel: number) => {
    const dataSlot = getSlots(
      storageConfig.hotels[hotel],
      plateAddresses.filter(
        (pa) => pa.moduleIdentifier == storageConfig.moduleIdentifier
      ),
      slotCorrectionResults
    );
    return new Array(dataSlot.length)
      .fill(null)
      .map((_, index) => ({
        moduleIdentifier: moduleIdentifier,
        hotelNumber: hotel + 1,
        slotNumber: index + 1,
      }))
      .filter(
        (sa) =>
          !dataSlot
            .filter((ds) => ds.disabled)
            .map((ds) => ds.id + 1)
            .includes(sa.slotNumber)
      );
  };

  const arrayHotelAddresses = () => {
    const allHotelAddresses = dataHotel.map((_, index) =>
      arraySlotAddresses(index)
    );
    return allHotelAddresses.flat();
  };

  return (
    <>
      <div className="slot-select-item" style={{ padding: "8px 0 0 12px" }}>
        <CheckBox
          checked={selectedHotel.length === dataHotel.length}
          id={`hotels-slot-correction`}
          name={`hotels-slot-correction`}
          onChange={() => handleSelectAllChange()}
          colorVariant="alternate"
          size="small"
        />
        <label>Select All</label>
      </div>
      <div className="slot-select">
        {dataHotel.map((item, index) => (
          <div className="slot-select-item">
            <CheckBox
              checked={selectedHotel.includes(
                `hotel${index + 1}-slot-correction`
              )}
              id={`hotel${index + 1}-slot-correction`}
              name={`hotel${index + 1}-slot-correction`}
              onChange={() => handleHotelChange(index)}
              colorVariant="alternate"
              size="small"
            />
            <label>{item.display.replace(/^Hotel\s*/, "")}</label>
          </div>
        ))}
      </div>
    </>
  );
}
